import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Edit2,
  Mail,
  X,
  UserCheck,
  UserX,
  Ban,
  ArrowLeft,
  User,
  Phone,
  Building2,
  Briefcase,
  MapPin,
  Shield,
  Calendar,
  Mail as MailIcon,
  Hash,
} from "lucide-react";
import { staffApi, type Staff } from "@/lib/api/staff";
import { staffCustomFieldsApi, type StaffCustomField } from "@/lib/api/staff-custom-fields";
import { cn } from "@/lib/utils";

const StaffDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [staff, setStaff] = useState<Staff | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [customFieldsConfig, setCustomFieldsConfig] = useState<StaffCustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<
    "resend" | "revoke" | "activate" | "deactivate" | "suspend" | null
  >(null);
  const [processing, setProcessing] = useState(false);

  const tenantId = user?.memberships?.[0]?.tenantId;

  useEffect(() => {
    if (isTenantAdmin && id) {
      loadStaff();
      loadCustomFieldsConfig();
    }
  }, [isTenantAdmin, id, tenantId]);

  const loadStaff = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await staffApi.getById(id);
      setStaff(data);
      loadPermissions(id);
    } catch (error) {
      console.error("Failed to load staff:", error);
      toast.error("Unable to load staff member. Please try again.");
      navigate("/setup/staff");
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async (staffId: string) => {
    try {
      setLoadingPermissions(true);
      const perms = await staffApi.getPermissions(staffId);
      setPermissions(perms);
    } catch (error) {
      console.error("Failed to load permissions:", error);
      // Don't show error toast - permissions are optional
    } finally {
      setLoadingPermissions(false);
    }
  };

  const loadCustomFieldsConfig = async () => {
    if (!tenantId) return;
    try {
      const config = await staffCustomFieldsApi.getConfig(tenantId);
      setCustomFieldsConfig(config.customFields || []);
    } catch (error) {
      console.error("Failed to load custom fields config:", error);
    }
  };

  const openActionDialog = (type: "resend" | "revoke" | "activate" | "deactivate" | "suspend") => {
    setActionType(type);
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (!staff || !actionType) return;

    try {
      setProcessing(true);
      switch (actionType) {
        case "resend":
          await staffApi.resendInvite(staff.id);
          toast.success(`Invitation resent to ${staff.email}`);
          break;
        case "revoke":
          await staffApi.revokeInvite(staff.id);
          toast.success(`Invitation revoked for ${staff.email}`);
          break;
        case "activate":
          await staffApi.activate(staff.id);
          toast.success(`${staff.firstName} ${staff.lastName} has been activated`);
          break;
        case "deactivate":
          await staffApi.deactivate(staff.id);
          toast.success(`${staff.firstName} ${staff.lastName} has been deactivated`);
          break;
        case "suspend":
          await staffApi.suspend(staff.id);
          toast.success(`${staff.firstName} ${staff.lastName} has been suspended`);
          break;
      }
      await loadStaff();
      setActionDialogOpen(false);
      setActionType(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to perform action. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      PENDING: "bg-yellow-100 text-yellow-800",
      ACTIVE: "bg-green-100 text-green-800",
      INACTIVE: "bg-gray-100 text-gray-800",
      SUSPENDED: "bg-red-100 text-red-800",
    };
    return badges[status] || badges.INACTIVE;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCustomFieldValue = (field: StaffCustomField, value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    switch (field.type) {
      case "checkbox":
        return value ? "Yes" : "No";
      case "date":
        return formatDate(value);
      case "select":
      case "radio":
        const option = field.options?.find((opt) => opt.value === value);
        return option?.label || String(value);
      default:
        return String(value);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to view staff details.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading staff member..." />
        </div>
      </AppLayout>
    );
  }

  if (!staff) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Staff Member Not Found</h2>
            <p className="mb-4 text-gray-600">The staff member you're looking for doesn't exist.</p>
            <Button onClick={() => navigate("/setup/staff")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Staff List
            </Button>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const customFields = staff.profileMetadata?.customFields || {};

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title={`${staff.firstName} ${staff.lastName}`}
          description="Staff member details and information"
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Staff", href: "/setup/staff" },
            { label: `${staff.firstName} ${staff.lastName}` },
          ]}
          showSearch={false}
        />

        {/* Action Buttons */}
        <div className="flex items-center justify-between mb-6">
          <Button onClick={() => navigate("/setup/staff")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Staff List
          </Button>
          <div className="flex items-center gap-2">
            {staff.status === "PENDING" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => openActionDialog("resend")}
                  className="gap-1.5"
                >
                  <Mail className="w-4 h-4" />
                  Resend Invite
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openActionDialog("revoke")}
                  className="gap-1.5"
                >
                  <X className="w-4 h-4" />
                  Revoke Invite
                </Button>
              </>
            )}
            {staff.status === "ACTIVE" && (
              <Button
                variant="outline"
                onClick={() => openActionDialog("deactivate")}
                className="gap-1.5"
              >
                <UserX className="w-4 h-4" />
                Deactivate
              </Button>
            )}
            {staff.status === "INACTIVE" && (
              <Button
                variant="outline"
                onClick={() => openActionDialog("activate")}
                className="gap-1.5"
              >
                <UserCheck className="w-4 h-4" />
                Activate
              </Button>
            )}
            {staff.status !== "SUSPENDED" && staff.status !== "PENDING" && (
              <Button
                variant="outline"
                onClick={() => openActionDialog("suspend")}
                className="gap-1.5"
              >
                <Ban className="w-4 h-4" />
                Suspend
              </Button>
            )}
            <Button onClick={() => navigate(`/setup/staff/${staff.id}/edit`)} className="gap-1.5">
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="p-6 bg-white shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-500">First Name</label>
                  <p className="mt-1 text-gray-900">{staff.firstName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Name</label>
                  <p className="mt-1 text-gray-900">{staff.lastName}</p>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                    <MailIcon className="w-4 h-4" />
                    Email
                  </label>
                  <p className="mt-1 text-gray-900">{staff.email}</p>
                </div>
                {staff.phone && (
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                      <Phone className="w-4 h-4" />
                      Phone
                    </label>
                    <p className="mt-1 text-gray-900">{staff.phone}</p>
                  </div>
                )}
                {staff.staffId && (
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                      <Hash className="w-4 h-4" />
                      Staff ID
                    </label>
                    <p className="mt-1 text-gray-900">{staff.staffId}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    <span
                      className={cn(
                        "inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium",
                        getStatusBadge(staff.status)
                      )}
                    >
                      {staff.status}
                    </span>
                  </p>
                </div>
              </div>
            </Card>

            {/* Assignment Information */}
            <Card className="p-6 bg-white shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Assignment</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                    <Building2 className="w-4 h-4" />
                    Department
                  </label>
                  <p className="mt-1 text-gray-900">{staff.department?.name || "—"}</p>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                    <Briefcase className="w-4 h-4" />
                    Job Title
                  </label>
                  <p className="mt-1 text-gray-900">{staff.jobTitle?.name || "—"}</p>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                    <Shield className="w-4 h-4" />
                    Role
                  </label>
                  <p className="mt-1 text-gray-900">{staff.role?.name || "—"}</p>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                    <MapPin className="w-4 h-4" />
                    Branch
                  </label>
                  <p className="mt-1 text-gray-900">{staff.branch?.name || "No branch assigned"}</p>
                </div>
              </div>
            </Card>

            {/* Custom Fields */}
            {customFieldsConfig.length > 0 && Object.keys(customFields).length > 0 && (
              <Card className="p-6 bg-white shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Additional Information</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {customFieldsConfig.map((field) => {
                    const value = customFields[field.name];
                    if (value === undefined || value === null || value === "") {
                      return null;
                    }
                    return (
                      <div key={field.name}>
                        <label className="text-sm font-medium text-gray-500">{field.label}</label>
                        <p className="mt-1 text-gray-900">{formatCustomFieldValue(field, value)}</p>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <Card className="p-6 bg-white shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Status</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Current Status</label>
                  <p className="mt-1">
                    <span
                      className={cn(
                        "inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium",
                        getStatusBadge(staff.status)
                      )}
                    >
                      {staff.status}
                    </span>
                  </p>
                </div>
                {staff.status === "PENDING" && (
                  <p className="text-sm text-gray-500">
                    Invitation sent. Waiting for staff member to accept and set password.
                  </p>
                )}
              </div>
            </Card>

            {/* Permissions */}
            {permissions.length > 0 && (
              <Card className="p-6 bg-white shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Permissions</h3>
                <div className="space-y-2">
                  {loadingPermissions ? (
                    <LoadingSpinner size="sm" label="Loading permissions..." />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {permissions.map((permission) => (
                        <span
                          key={permission}
                          className="px-2 py-1 text-xs font-medium bg-primary/5 text-primary rounded"
                        >
                          {permission}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Timestamps */}
            <Card className="p-6 bg-white shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Timestamps</h3>
              <div className="space-y-3">
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                    <Calendar className="w-4 h-4" />
                    Created
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(staff.createdAt)}</p>
                </div>
                <div>
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
                    <Calendar className="w-4 h-4" />
                    Last Updated
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(staff.updatedAt)}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </PageContainer>

      {/* Action Confirmation Dialog */}
      <ConfirmDialog
        open={actionDialogOpen}
        onOpenChange={setActionDialogOpen}
        title={
          actionType === "resend"
            ? "Resend Invitation"
            : actionType === "revoke"
              ? "Revoke Invitation"
              : actionType === "activate"
                ? "Activate Staff Member"
                : actionType === "deactivate"
                  ? "Deactivate Staff Member"
                  : "Suspend Staff Member"
        }
        description={
          actionType === "resend"
            ? `Are you sure you want to resend the invitation email to ${staff?.email}?`
            : actionType === "revoke"
              ? `Are you sure you want to revoke the invitation for ${staff?.email}? They will need a new invitation to join.`
              : actionType === "activate"
                ? `Are you sure you want to activate ${staff?.firstName} ${staff?.lastName}?`
                : actionType === "deactivate"
                  ? `Are you sure you want to deactivate ${staff?.firstName} ${staff?.lastName}?`
                  : `Are you sure you want to suspend ${staff?.firstName} ${staff?.lastName}?`
        }
        confirmLabel={
          actionType === "resend"
            ? "Resend"
            : actionType === "revoke"
              ? "Revoke"
              : actionType === "activate"
                ? "Activate"
                : actionType === "deactivate"
                  ? "Deactivate"
                  : "Suspend"
        }
        cancelLabel="Cancel"
        onConfirm={handleAction}
        variant={actionType === "revoke" || actionType === "suspend" ? "danger" : "info"}
        loading={processing}
      />
    </AppLayout>
  );
};

export default StaffDetailsPage;
